import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../styles/legal.css';

const TermsPage = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-header-content">
          <h1 className="legal-title">Terms of Service</h1>
          <p className="legal-updated">Last updated: April 30, 2026</p>
        </div>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Agreement to Terms</h2>
          <p>
            By accessing or using services provided by Core Air Life Fitness (dba GoCalFit, Cal Fit) ("we," "us," "our"), you agree to these Terms.
          </p>
          <p className="legal-emphasis">If you do not agree, do not use our services.</p>
        </section>

        <section className="legal-section">
          <h2>2. Services</h2>
          <p>We provide fitness experiences including (but not limited to):</p>
          <ul>
            <li>Bungee fitness classes</li>
            <li>Aerial programs and camps for youth</li>
            <li>Open studio sessions</li>
            <li>Coaching and consulting</li>
          </ul>
          <p>All services are subject to availability and may change at any time.</p>
        </section>

        <section className="legal-section">
          <h2>3. Eligibility</h2>
          <p>You must:</p>
          <ul>
            <li>Be at least 18 years old to participate independently</li>
            <li>Have a parent/guardian consent for minors</li>
            <li>Be physically able to participate safely</li>
          </ul>
          <p>We reserve the right to refuse participation if safety is a concern.</p>
        </section>

        <section className="legal-section legal-warning">
          <h2>4. Assumption of Risk</h2>
          <p>You understand and agree that:</p>
          <p className="legal-highlight">
            Participation in fitness activities—including bungee, aerial, and related movement—carries inherent risks, including injury, illness, or death.
          </p>
          <p className="legal-emphasis">By participating, you voluntarily assume all risks associated with these activities.</p>
        </section>

        <section className="legal-section legal-warning">
          <h2>5. Waiver + Release</h2>
          <p>To the fullest extent permitted by law:</p>
          <p className="legal-highlight">
            You release and hold harmless Core Air Life Fitness, its owners, instructors, and affiliates from any and all claims, liabilities, damages, or expenses arising from your participation. This includes claims based on negligence.
          </p>
          
          <h3>Required Waiver</h3>
          <p>Participation in any Core Air Life Fitness services requires completion of a separate Waiver and Release of Liability and Media Consent form.</p>
          <ul>
            <li>This waiver must be completed prior to participation in any class, session, camp, or event.</li>
            <li>For minors, a parent or legal guardian must complete and sign the waiver on their behalf.</li>
            <li>Failure to complete the required waiver will result in denied participation without refund.</li>
          </ul>
          <p>By agreeing to these Terms, you acknowledge that:</p>
          <ul>
            <li>You are required to complete the waiver</li>
            <li>The waiver governs your participation</li>
            <li>The waiver includes a full release of liability and assumption of risk</li>
          </ul>
          <div className="legal-waiver-cta">
            <Link to="/waiver" className="legal-waiver-btn">Complete Waiver Form →</Link>
          </div>
        </section>

        <section className="legal-section">
          <h2>6. Medical Responsibility</h2>
          <p>You are responsible for:</p>
          <ul>
            <li>Knowing your physical limits</li>
            <li>Disclosing relevant injuries or conditions</li>
            <li>Seeking medical clearance if needed</li>
          </ul>
          <p>We are not medical providers and do not give medical advice.</p>
          <p>We may take reasonable action in an emergency, but you accept responsibility for any resulting care or costs.</p>
        </section>

        <section className="legal-section">
          <h2>7. Payments + Bookings</h2>
          <ul>
            <li>All bookings are subject to availability</li>
            <li>Payment is required to confirm participation</li>
            <li>Pricing may change at any time</li>
          </ul>
          <p>We use third-party providers (e.g., Stripe, PayPal, Acuity) to process payments and scheduling.</p>
        </section>

        <section className="legal-section">
          <h2>8. Cancellations + Refunds</h2>
          <p>Policies may vary by service, but generally:</p>
          <ul>
            <li>Cancellations within the stated window may be credited or rescheduled</li>
            <li>Late cancellations or no-shows may be forfeited</li>
            <li>Camps and special events may have stricter policies</li>
          </ul>
          <p>Specific terms will be communicated at time of booking.</p>
        </section>

        <section className="legal-section">
          <h2>9. Code of Conduct</h2>
          <p>We're building a strong, supportive environment.</p>
          <p>You agree to:</p>
          <ul>
            <li>Follow instructor guidance</li>
            <li>Treat others with respect</li>
            <li>Use equipment responsibly</li>
          </ul>
          <p>We reserve the right to remove anyone without refund if behavior is unsafe or disruptive.</p>
        </section>

        <section className="legal-section">
          <h2>10. Media Release</h2>
          <p>By participating, you grant permission for us to capture and use photos/videos for marketing, unless you notify us otherwise in writing.</p>
        </section>

        <section className="legal-section">
          <h2>11. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law:</p>
          <p className="legal-highlight">
            Our total liability to you for any claim is limited to the amount you paid for the service.
          </p>
          <p>We are not liable for:</p>
          <ul>
            <li>Indirect or consequential damages</li>
            <li>Lost income or opportunities</li>
            <li>Personal property loss or damage</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>12. Indemnification</h2>
          <p>You agree to indemnify and hold us harmless from any claims arising from:</p>
          <ul>
            <li>Your participation</li>
            <li>Your actions</li>
            <li>Your violation of these Terms</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>13. Third-Party Services</h2>
          <p>We are not responsible for third-party platforms (payment processors, booking systems, partners, offsite activities).</p>
          <p>Use of those services is at your own risk.</p>
        </section>

        <section className="legal-section">
          <h2>14. Disputes</h2>
          <p>Any disputes will be handled in the state of North Carolina.</p>
          <p>We encourage resolution through direct communication first.</p>
        </section>

        <section className="legal-section">
          <h2>15. Changes to Terms</h2>
          <p>We may update these Terms at any time. Continued use of services means you accept those changes.</p>
        </section>

        <section className="legal-section">
          <h2>16. Contact</h2>
          <p>Questions? Reach out:</p>
          <p><a href="mailto:hello@gocalfit.com" className="legal-contact-link">hello@gocalfit.com</a></p>
        </section>
      </div>
    </div>
  );
};

export default TermsPage;
